import { getConfig } from "../config";

/**
 * The support assistant is an opt-in feature gated by a Key Vault secret.
 *
 * The secret `SupportAssistantEnabled` flows into `window.config.supportAssistantEnabled`
 * by the same path as the Analytics flag: `config.aspx` (server env var
 * `SUPPORT_ASSISTANT_ENABLED`) where that is executed, or the
 * `__SUPPORT_ASSISTANT_ENABLED__` token the pipeline injects into index.html at build time
 * (the real path on Azure Static Web Apps).
 *
 * OFF IS THE DEFAULT, AND THAT IS THE POINT
 * -----------------------------------------
 * Anything other than an affirmative value — empty, "N", an absent secret, or the
 * unreplaced token in local dev — keeps **every** trace of the assistant hidden: the
 * launcher button, its icon, the panel, and the code that would call the model. A vault that
 * has never heard of this secret therefore ships an app that looks exactly as it does today.
 * That is the opposite of `corpAccessGroups`, where an absent value means "no gate" — here
 * an absent value means "no feature", because a half-configured AI feature that renders a
 * button and then errors is worse than one that was never there.
 *
 * "Y" is the house convention (it matches `AnalyticsEnabled` and `SupportAutoTenantEnabled`),
 * but "true" is accepted too: this flag was specified as "enabled when the value returns
 * true", and accepting both spellings can only ever turn the feature ON for someone who
 * meant to. It can never lock anybody out of anything.
 *
 * Read lazily via getConfig() so it always reflects the live window.config and stays
 * mockable in tests.
 */
export const isSupportAssistantEnabled = (): boolean => {
  const value = (getConfig()?.supportAssistantEnabled ?? "").trim().toUpperCase();
  return value === "Y" || value === "TRUE";
};

/*
 * `ANALYTICS_ROUTE` used to live here, and went with the rule that was its only reader
 * (2026-09-15). It existed so the launcher could be kept OFF the reporting screen, and the only
 * thing it ever had to get right was the capital A — react-router is case-sensitive, so a
 * lower-cased literal would silently never match. The launcher is a header control now and is on
 * the Analytics screen like every other utility, so there is nothing left to compare. The three
 * places that still name that route (`goAnalytics` in `CSMShopfloorHeader`, the `showRibbon` set
 * in `layouts/Default`, and the screen table in `utility/routes.ts`) each spell their own
 * literal and always did; this constant was never one of them to keep in step.
 */

/**
 * ⚠️ EVERY FIELD HERE IS RETIRED, AND THE INTERFACE IS KEPT ON PURPOSE (2026-09-15).
 *
 * These are the three inputs the rule below USED to consider, and naming them is what lets the
 * reversal be pinned rather than merely described: `supportAssistantAccess.test.ts` still drives
 * all eight combinations and now asserts `true` for every one, so the four rows that flipped are
 * visible as flipped rather than as tests somebody quietly deleted.
 *
 * Nothing reads them — see the note on the predicate for why none of the three survived the move
 * into the header.
 */
export interface SupportLauncherContext {
  /** Manager Mode is on (`appSettings.isManagerMode`). */
  isManagerMode: boolean;

  /** The user is on the Analytics/reporting screen. */
  isAnalyticsRoute: boolean;

  /** The Submit Feedback drawer is open (`appSettings.isDiagnosticPaneOpen`). */
  isDiagnosticPaneOpen: boolean;
}

/**
 * WHERE THE CHAT LAUNCHER IS ALLOWED TO APPEAR: wherever the header renders it.
 *
 * | Where                              | Launcher |
 * |------------------------------------|----------|
 * | anywhere the header is drawn       | yes      |
 *
 * ⚠️ IT USED TO BE `(isManagerMode && !isAnalyticsRoute) || isDiagnosticPaneOpen`, AND BOTH
 * SURVIVING CLAUSES WERE DELETED DELIBERATELY WHEN THE LAUNCHER MOVED INTO THE HEADER
 * (2026-09-15). Neither was a rule about who may ask for help; both were rules about a control
 * that FLOATED, and the argument for each died with the `position: fixed` it was protecting:
 *
 * - **`isManagerMode` (with the drawer as the operator's way in).** A 48px pill parked in the
 *   bottom-right corner of an operator's board is in the way of someone running a job, so it was
 *   rationed to managers and to the one moment an operator had already reached for help — the
 *   **Submit Feedback** drawer. In the header it is in the way of nothing: it sits in the utility
 *   row beside Images, Documents, the account menu and the gear, none of which hide themselves
 *   from an operator. And the requirement it was costing us is the plain one — shop-floor users
 *   are exactly the people who most need to ask how to do something, and "open the feedback
 *   drawer first" is not a discoverable route to a chat panel.
 * - **`isAnalyticsRoute`.** Analytics was excluded in both modes because the reporting screen
 *   uses the full width and the floating pill overlapped the charts and the grid's own controls.
 *   A header control overlaps nothing; the header is drawn above that screen exactly as it is
 *   above every other.
 *
 * The drawer has not stopped mattering — it changed direction. It no longer decides whether the
 * launcher EXISTS; it now makes the panel open itself (`SupportAssistant`'s auto-open effect), so
 * somebody heading for Submit Feedback is offered the assistant rather than having to notice a
 * button that just appeared. It is also still what shifts the open panel clear of the drawer's
 * overlay (`Panel`'s `$offset`), which is a layout fact and has nothing to do with this.
 *
 * ⚠️ THE FUNCTION STAYS, AND SO DOES ITS PARAMETER, even though the body no longer reads either.
 * This is the documented seam between "the header may draw it" and the rule about where the
 * assistant is offered; a rule that has become "always" is still a rule, and inlining `true` at
 * the call site would leave the next narrowing with nowhere to go and nothing pinned. The
 * parameter is what keeps the retired truth table expressible in the tests — see
 * `SupportLauncherContext`.
 *
 * NOTE this deliberately does NOT check `isSupportAssistantEnabled()`. That flag decides
 * whether the feature exists at all and is checked first and separately, so that an
 * environment without the secret renders nothing whatever this returns.
 */
export const shouldShowSupportLauncher = (
  _retired?: SupportLauncherContext
): boolean => true;

export interface ScreenHelpContext {
  /**
   * `screenForPath` matched the current URL to a row in `utility/routes.ts`.
   *
   * False on `/login`, `/logout`, `termsandconditions`, `consent-details` and `/diagnostic` —
   * places the assistant has nothing to say about, and which the screen table deliberately
   * omits. The caller passes the RESULT of that lookup rather than the pathname, so there is
   * still exactly one path matcher in the app.
   */
  hasScreen: boolean;

  /** `canReachScreen` said yes for that screen and this user. */
  userCanReachScreen: boolean;
}

/**
 * WHERE THE PER-SCREEN "?" IS ALLOWED TO APPEAR.
 *
 * | Where                                        | "?"   |
 * |----------------------------------------------|-------|
 * | A mapped screen the user can reach           | yes   |
 * | A mapped screen the user CANNOT reach        | no    |
 * | An unmapped path (/login, TOS, /diagnostic)  | no    |
 *
 * ⚠️ THIS IS A SEPARATE PREDICATE FROM `shouldShowSupportLauncher` AND MUST STAY ONE, EVEN NOW
 * THAT THEY AGREE ON EVERY SCREEN IN THE APP. They answer different questions. The launcher is
 * an *invitation to chat*, and you can ask a question from anywhere, so it depends on nothing.
 * The "?" is an *answer about the screen you are looking at*, so it depends on there being a
 * screen: a path the table does not map has nothing to tour, and a screen this user cannot
 * reach must never be offered — the assistant may not name a door it knows is locked. Folding
 * them together would gate the chat on a walkthrough existing and on a screen-level access
 * check that has nothing to do with asking a question.
 *
 * (They used to disagree loudly — the launcher was off for an operator and off on Analytics,
 * where the "?" was required. That was a rule about a floating pill and it is gone; the
 * agreement is a coincidence of today's rules, not a reason to merge them.)
 *
 * The one rule that is shared, and why:
 *
 * - **Access.** An operator must never be shown a "?" for a manager screen. In practice the
 *   router would have bounced them before the button could render, so this is a second line
 *   rather than the only one — but the whole feature turns on never naming a door the user
 *   cannot open, and a control that is present-then-bounces reads as the app being wrong.
 *
 * ⚠️ THE FEEDBACK DRAWER NO LONGER HIDES IT, AND THAT CLAUSE WAS DELETED DELIBERATELY
 * (2026-09-15). It was never a rule about help; it was a rule about floating things. While the
 * "?" was `position: fixed` in the bottom-right corner it landed on top of the drawer's
 * full-viewport overlay, beside a launcher that had already shifted over to stay clickable —
 * three controls stacked in one corner over a screen nobody could see. The "?" is now an
 * ordinary control in the app header (`CSMShopfloorHeader`), which the drawer's overlay covers
 * exactly as it covers Images, Documents, the account menu and the gear — and *none* of those
 * remove themselves when the drawer opens. Keeping the clause would have made the header change
 * shape behind a modal and change back when it closed, which on a phone (where the utility row
 * wraps) is a visible reflow of the whole header. So the drawer is now simply not this
 * predicate's business.
 *
 * It is not the launcher's either, a day later and for the same reason — but it did not stop
 * mattering, it changed what it does. The drawer now OPENS THE PANEL (`SupportAssistant`'s
 * auto-open effect), which is behaviour rather than visibility and is why it is no longer an
 * input to any predicate in this file.
 *
 * Like `shouldShowSupportLauncher`, this deliberately does NOT check
 * `isSupportAssistantEnabled()`. The flag decides whether the feature exists at all and is
 * checked first and separately, so an environment without the secret renders nothing whatever
 * this returns.
 */
export const shouldShowScreenHelp = ({
  hasScreen,
  userCanReachScreen,
}: ScreenHelpContext): boolean => hasScreen && userCanReachScreen;
